import {
	differenceInCalendarDays,
	startOfDay,
	startOfWeek,
	subDays,
} from "date-fns";
import { useMemo } from "react";
import type { TimelineItemResponseDTO as TimelineItem } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type {
	GroupAccumulator,
	RecentGroupedTimeline,
	TFunction,
} from "web_myplann/NotificationCenter/Utils/groupTypes";
import { parseTimelineDate } from "web_myplann/NotificationCenter/Utils/parseTimelineDate";
import {
	classifyRecentDate,
	EARLIER_WEEK_HORIZON_DAYS,
	formatMonthLabel,
	formatWeekLabel,
	WEEK_OPTIONS,
	type WeekLabels,
} from "web_myplann/NotificationCenter/Utils/recentGroupBuckets";
import { sortTimelineItemsDescending } from "web_myplann/NotificationCenter/Utils/sortTimelineItems";
import { useDateFnsLocale } from "web_myplann/Shared/Hooks/useDateFnsLocale";
import { formatApiDate } from "web_myplann/Shared/Utils/apiDate";

export const useGroupRecentItems = (
	items: TimelineItem[],
	t: TFunction,
): RecentGroupedTimeline => {
	const locale = useDateFnsLocale();
	const todayKey = formatApiDate(new Date());
	return useMemo(() => {
		const today = startOfDay(new Date());
		const cutoffEarlierWeek = startOfWeek(
			subDays(today, EARLIER_WEEK_HORIZON_DAYS),
			WEEK_OPTIONS,
		);

		const todayLabel = t(Resources.NotificationCenter.RecentGroups.Today);
		const weekLabels: WeekLabels = {
			thisWeek: t(Resources.NotificationCenter.RecentGroups.ThisWeek),
			lastWeek: t(Resources.NotificationCenter.RecentGroups.LastWeek),
			weeksAgo: (count) =>
				t(Resources.NotificationCenter.RecentGroups.WeeksAgo, { count }),
		};

		const groupMap = new Map<string, GroupAccumulator>();

		for (const item of items) {
			const itemDate = parseTimelineDate(item.date);
			if (differenceInCalendarDays(itemDate, today) > 0) {
				continue;
			}
			const bucket = classifyRecentDate(itemDate, today, cutoffEarlierWeek);

			let accumulator = groupMap.get(bucket.key);
			if (!accumulator) {
				if (bucket.kind === "today") {
					accumulator = {
						key: bucket.key,
						label: todayLabel,
						items: [],
						sortDate: today,
					};
				} else if (
					bucket.kind === "thisWeek" ||
					bucket.kind === "earlierWeek"
				) {
					const weekStart =
						bucket.kind === "thisWeek"
							? startOfWeek(today, WEEK_OPTIONS)
							: bucket.representativeDate;
					accumulator = {
						key: bucket.key,
						label: formatWeekLabel(weekStart, today, weekLabels),
						items: [],
						sortDate: weekStart,
					};
				} else {
					accumulator = {
						key: bucket.key,
						label: formatMonthLabel(bucket.representativeDate, locale),
						items: [],
						sortDate: bucket.representativeDate,
					};
				}
				groupMap.set(bucket.key, accumulator);
			}
			accumulator.items.push(item);
		}

		const groups = Array.from(groupMap.values())
			.filter((group) => group.items.length > 0)
			.map((group) => ({
				key: group.key,
				label: group.label,
				items: sortTimelineItemsDescending(group.items),
				sortDate: group.sortDate,
			}))
			.sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime());

		return groups.map(({ key, label, items: groupItems }) => ({
			key,
			label,
			items: groupItems,
		}));
	}, [items, t, locale, todayKey]);
};
